import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuthenticator } from '@aws-amplify/ui-react';
import { GraphQLErrorResponse } from '@components/pages/commonType';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Typography
} from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { getAreaInfoApi, getSectionUsageStatusWithConditionApi } from '@apis/index';
import { MessageDialogState, SectionDialogInput, SectionDialogState } from '@atom/DialogState';
import { FacilityState } from '@atom/FacilityState';

import {
  GetAreaInfoQueryVariables,
  GetSectionUsageStatusWithConditionQueryVariables,
  getAreaInfoType
} from '../../../API';

const images = {
  maxHeight: '100%',
  maxWidth: '100%',
  height: 'auto',
  width: 'auto',
  'align-items': 'center',
  display: 'flex',
  justifyContent: 'center'
};
const cardsNoMaxHeight = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  overflow: 'hidden',
  boxShadow: 'none'
};

type SectionList = {
  sectionId: string;
  sectionName: string;
};

const SectionDialog = () => {
  /* 共通State */
  const setMessageDialogState = useSetRecoilState(MessageDialogState);
  const resetSectionDialogState = useResetRecoilState(SectionDialogState);

  const sectionDialogState: SectionDialogInput = useRecoilValue(SectionDialogState);

  const { sectionData, defSection, defStartTime, defEndTime, onLoginClick, onGuestClick } = sectionDialogState;
  const [searchParams] = useSearchParams();
  const areaIdParam = searchParams.get('areaId') ?? '';
  const facilityIdParam = searchParams.get('facilityId') ?? '';
  const [areaInfo, setAreaInfo] = useState<getAreaInfoType>();

  const [startTime, setStartTime] = useState<Dayjs | null>(dayjs().hour(0).minute(0));
  const [endTime, setEndTime] = useState<Dayjs | null>(dayjs().hour(0).minute(0));
  const [sections, setSections] = useState<string[]>([]);
  const [isAllSectionCheckd, setIsAllSectionCheckd] = useState<boolean>(false);

  const [invalidMessages, setInvalidMessages] = useState<string[]>([]);
  const [isWaitingCheck, setIsWaitingCheck] = useState<boolean>(false);

  /* 認証情報 */
  const { user } = useAuthenticator((context) => [context.user]);
  const [isLoginUser, setIsLoginUser] = useState<boolean>(false);
  const facilityState = useRecoilValue(FacilityState);
  const { facilityId } = facilityState;


  const handleClose = () => {
    resetSectionDialogState();
    setStartTime(dayjs().hour(0).minute(0));
    setEndTime(dayjs().hour(0).minute(0));
    setSections([]);
    setInvalidMessages([]);
  };

  // エリア情報取得
  const getData = async (facility: string, area: string) => {
    try {
      const input: GetAreaInfoQueryVariables = {
        input: {
          facilityId: facility,
          areaId: area
        }
      };

      // データ取得
      const result = await getAreaInfoApi(input);

      setAreaInfo(result);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);

      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in (error as GraphQLErrorResponse) && (error as GraphQLErrorResponse).errors.length !== 0) {
        message = (error as GraphQLErrorResponse).errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    }
  };

  // 予約情報取得
  const getReservationData = async (
    facility: string,
    area: string,
    inputSections: string[],
    targetDate: string,
    inputStartTime: dayjs.Dayjs,
    inputEndTime: dayjs.Dayjs
  ) => {
    setIsWaitingCheck(true);
    let result;
    try {
      const input: GetSectionUsageStatusWithConditionQueryVariables = {
        input: {
          facilityId: facility,
          areaId: area,
          sectionIds: inputSections,
          targetDate,
          targetStartTime: inputStartTime.format('HH:mm'),
          targetEndTime: inputEndTime.format('HH:mm')
        }
      };
      // データ取得
      result = await getSectionUsageStatusWithConditionApi(input);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);

      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in (error as GraphQLErrorResponse) && (error as GraphQLErrorResponse).errors.length !== 0) {
        message = (error as GraphQLErrorResponse).errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    } finally {
      setIsWaitingCheck(false);
    }
    return result;
  };

  const handleCheckboxChange = (section: string, isChecked: boolean) => {
    if (isChecked) {
      // チェックされたら、その項目をsections配列に追加
      setSections((prevSections) => [...prevSections, section]);
    } else {
      // チェックが外れたら、その項目をsections配列から削除
      setSections((prevSections) => prevSections.filter((s) => s !== section));
    }
  };

  const handleAllSectionCheck = (isChecked: boolean) => {
    setIsAllSectionCheckd(isChecked);
    if (isChecked) {
      // チェックされたら、全セクションをsections配列に追加
      setSections(sectionData.sectionList.map((sectionItem) => sectionItem.sectionId));
    } else {
      // チェックが外れたら、全セクションをsections配列から削除
      setSections([]);
    }
  };

  const validationCheck = async (loginType: number) => {
    // 初期化
    setInvalidMessages([]);
    const tempInvalidMessages = [];

    // 区画が選択されていなければエラー
    if (sections.length === 0) {
      tempInvalidMessages.push('区画を選択してください');
    }

    // 時刻が逆転していればエラー
    if (startTime?.isAfter(endTime) || (startTime?.hour() === endTime?.hour() && startTime?.minute() === endTime?.minute())) {
      tempInvalidMessages.push('開始・終了時間が逆転しているか同じです');
    }

    if (tempInvalidMessages.length === 0) {
      // 指定された時間が使用不可だった場合エラー
      if (
        startTime &&
        endTime &&
        sectionDialogState.availableStartTime <= startTime &&
        sectionDialogState.availableEndTime >= endTime
      ) {
        await getReservationData(
          facilityIdParam,
          areaIdParam,
          sections,
          sectionDialogState.date,
          startTime,
          endTime
        ).then((res) => {
          if (res) {
            if (res.unavailableList.length > 0) {
              res.unavailableList.forEach((unavailableItem) => {tempInvalidMessages.push(unavailableItem.message)});
            } else {
              // 0の場合はログイン
              if (loginType === 0) {
                onLoginClick?.(startTime?.format('HH:mm') || '', endTime?.format('HH:mm') || '', sections);
              } else {
                onGuestClick?.(startTime?.format('HH:mm') || '', endTime?.format('HH:mm') || '', sections);
              }
              handleClose();
            }
          } else {
            tempInvalidMessages.push('予約状況の取得に失敗しました');
          }
        });
      } else {
        tempInvalidMessages.push(
          `営業時間内(${sectionDialogState.availableStartTime.format('HH:mm')}〜${sectionDialogState.availableEndTime.format('HH:mm')})で時間を選択してください`
        );
      }
    }
    setInvalidMessages(tempInvalidMessages);
  };

  useEffect(() => {
    setSections([defSection]);
    setStartTime(defStartTime);
    setEndTime(defEndTime);
  }, [sectionDialogState]);

  useEffect(() => {
    // ログインユーザーかどうか判定する
    setIsLoginUser(user !== undefined);
  }, [user]);

  useEffect(() => {
    // facilityId と areaId が有効な場合にのみ API を呼び出す
    if (facilityIdParam && areaIdParam) {
      getData(facilityIdParam, areaIdParam);
    }
  }, [facilityIdParam, areaIdParam]);

  useEffect(() => {
    // 選択された区画とAPIから取得した区画の数が一致する場合、全区画を選択するにチェックをいれる
    if (sections.length === sectionData.sectionList.length) {
      setIsAllSectionCheckd(true);
    } else {
      // それ以外はチェックを外す
      setIsAllSectionCheckd(false);
    }
  }, [sections]);

  // TODO MUIベースで作り直す
  return (
    <Dialog open={sectionDialogState.open} onClose={handleClose}>
      <DialogTitle sx={{ m: 0, p: 2 }} id="section-dialog-title">
        <Typography variant="h5" gutterBottom>
          {sectionData.title}
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          利用区画
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card style={{ padding: 16, ...images }} elevation={0}>
              <img
                src={`/images/${facilityId}/section/${areaInfo?.sectionImagePath}`}
                alt="sectionImages"
                style={images}
              />
            </Card>
          </Grid>
          <Grid item xs={12} textAlign="center">
            {invalidMessages.map((invalidMessage) => (
              <Typography sx={{ color: 'red' }}>{invalidMessage}</Typography>
            ))}
          </Grid>
          <Grid item xs={12} textAlign="center">
            <Box mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(event) => handleAllSectionCheck(event.target.checked)}
                    checked={isAllSectionCheckd}
                  />
                }
                label={<Typography style={{ wordBreak: 'break-word' }}>全区画を選択する</Typography>}
              />
            </Box>
            <Card style={{ ...cardsNoMaxHeight }} elevation={0}>
              <Box component="fieldset" borderColor="transparent">
                <Grid container spacing={2} justifyContent="flex-start" alignItems="center" wrap="wrap">
                  {sectionData.sectionList.map((section: SectionList) => (
                    <Grid item xs={6} sm={3} key={section.sectionId} style={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(event) => handleCheckboxChange(section.sectionId, event.target.checked)}
                            defaultChecked={section.sectionId === defSection}
                            checked={sections.includes(section.sectionId)}
                          />
                        }
                        label={<Typography style={{ wordBreak: 'break-word' }}>{section.sectionName}</Typography>}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" style={{ flexShrink: 1 }} gap={2}>
              <DesktopTimePicker
                label="開始時間"
                minutesStep={60}
                ampm={false}
                value={startTime}
                onChange={(newValue) => {
                  setStartTime(newValue);
                }}
                skipDisabled
              />
              <Typography>〜</Typography>
              <DesktopTimePicker
                label="終了時間"
                minutesStep={60}
                ampm={false}
                value={endTime}
                onChange={(newValue) => {
                  setEndTime(newValue);
                }}
                skipDisabled
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography textAlign="right">(1時間あたり/税込) {areaInfo?.minFee}円 〜</Typography>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              style={{
                backgroundColor: '#02ada9',
                color: 'white',
                borderRadius: '6px',
                fontSize: '20px'
              }}
              onClick={() => {
                validationCheck(0);
              }}
              disabled={isWaitingCheck}
            >
              {isWaitingCheck && <CircularProgress color="inherit" />}
              {!isWaitingCheck && isLoginUser && '予約'}
              {!isWaitingCheck && !isLoginUser && 'ログインして予約'}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default SectionDialog;
